import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
// @ts-ignore country-iso-3-to-2
import getCountryISO2 from 'country-iso-3-to-2';
import { useNavigate, useParams } from 'react-router-dom';

import {
  International,
  Statistic,
  useInternational,
} from '../../../store/international';
import InternationalMap from './Map';
import { useInternationalList } from './useInternationalList';
import Comments from '../../Common/Comments';

import styles from './index.module.scss';
import { IconCommunity } from '../../Common/Icon';
import { useStatisticList } from './useStatistic';
import DisplayHtmlContent from '../../Common/DisplayHtmlContent';

const getFlagUrlFromIso3 = (iso3: string): string => {
  const iso2: string = getCountryISO2(iso3);

  return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${iso2}.svg`;
};

function InternationalContent() {
  const { internationalId } = useParams();
  const navigate = useNavigate();
  const { getInternationals } = useInternationalList();
  const { getStatistics } = useStatisticList();
  const { list, statistic } = useInternational();
  const [international, setInternational] = useState<International | null>(
    null,
  );
  const [internationalStatistics, setInternationalStatistics] = useState<null | Statistic>(null);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    getInternationals({ page: 1, pageSize: 100, search });
  }, [search]);

  useEffect(() => {
    getStatistics({});
  }, []);

  useEffect(() => {
    if (international && statistic.response?.length) {
      setInternationalStatistics(
        statistic.response.find(({ id }) => id === international?.country.id)
          || null,
      );
    }
  }, [international, statistic.response]);

  const markers = useMemo(
    () => list.response?.data.map(({ country, id }) => {
      const stats = statistic.response?.find(
        ({ id: sid }) => sid === country.id,
      );

      return {
        id,
        countryCode: country.iso3,
        avarage: Math.round(Number.parseFloat(stats?.medianPercentValue || '0')),
      };
    }) || [],
    [list.response?.data, statistic.response],
  );

  useEffect(() => {
    if (list.response?.data.length) {
      setInternational(
        internationalId
          ? list.response?.data.find(({ id }) => id === internationalId) || null
          : null,
      );
    }
  }, [internationalId, list.response?.data]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.mapWrapper}>
        <div className={styles.map}>
          <InternationalMap
            markers={markers}
            selectedCountryIso3={international?.country.iso3}
          />
        </div>
        <div className={styles.headerWrapper}>
          <div className={styles.header}>
            <h2 className={styles.headerTitle}>
              International <span className={styles.textGreen}>Community</span>
            </h2>

            <Select
              placeholder="Select Country"
              className={styles.select}
              value={internationalId || undefined}
              allowClear
              showSearch
              onSearch={setSearch}
              filterOption={(inputValue, option) => !!option?.label.toLowerCase().includes(inputValue.toLowerCase())}
              options={list.response?.data.map(({ id, country }) => ({
                label: country.name,
                value: id,
              }))}
              onChange={(value: string) => navigate(`/international${value ? `/${value}` : ''}`)}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <h3 className={styles.title}>
            Tipping at Restaurants Around the World
          </h3>
          <p className={styles.description}>
            This data is based on responses from our international users
          </p>
        </div>
      </div>

      <div className={styles.container}>
        {internationalId ? (
          <div className={styles.countryInfo}>
            <div className={styles.largeContainer}>
              <p className={styles.description}>
                According to TippingGuide.com users in{' '}
                {international?.country.name}:
              </p>

              <div className={styles.statisticWrapper}>
                <div className={styles.statisticItem}>
                  <div className={styles.value}>
                    {Math.round(
                      Number.parseFloat(
                        internationalStatistics?.medianPercentValue || '0',
                      ),
                    )}
                    %
                  </div>
                  <div className={styles.label}>
                    is the average tip at dine-in restaurants in{' '}
                    {international?.country.name}.
                  </div>
                </div>
                <div className={styles.statisticItem}>
                  <div className={styles.value}>
                    {Math.round(
                      Number.parseFloat(
                        internationalStatistics?.zeroPercentValue || '0',
                      ),
                    )}
                    %
                  </div>
                  <div className={styles.label}>
                    people never tip at restaurants in{' '}
                    {international?.country.name}.
                  </div>
                </div>
              </div>
            </div>

            {international?.description ? (
              <DisplayHtmlContent
                html={international.description}
                className={styles.smallContainer}
              />
            ) : null}

            <div className={styles.joinConversation}>
              <IconCommunity className={styles.icon} />
              <span className={styles.text}>
                Join the conversation here to discuss tipping in{' '}
                {international?.country.name}!
              </span>
              {international?.country.iso3 && (
                <img
                  src={getFlagUrlFromIso3(international.country.iso3)}
                  alt="flag"
                  className={styles.flag}
                  width={24}
                  height={16}
                />
              )}
            </div>
          </div>
        ) : (
          <div className={styles.countryList}>
            <div className={styles.listHeader}>
              <div className={styles.headerIndex}>#</div>
              <div
                className={styles.headerTitle}
                style={{ textAlign: 'start' }}
              >
                Country
              </div>
              <div className={styles.headerTitle}>Average Customer Tip</div>
            </div>
            {(statistic.response ? [...statistic.response] : [])
              .filter(({ id }) => {
                const currentInternational = list.response?.data.find(
                  ({ country }) => country.id === id,
                );

                return currentInternational?.isActive;
              })
              .map(({ id, countryName, medianPercentValue }, i) => {
                const currentInternational = list.response?.data.find(
                  ({ country }) => country.id === id,
                );

                const iso3 = currentInternational?.country.iso3 || '';
                const flagUrl = iso3 ? getFlagUrlFromIso3(iso3) : '';

                return (
                  <button
                    type="button"
                    key={id}
                    className={clsx(
                      styles.listRow,
                      id === international?.country.id && styles.selectedRow,
                    )}
                    onClick={() => {
                      navigate(
                        `/international/${currentInternational?.id || ''}`,
                      );
                    }}
                  >
                    <div className={styles.rowValueIndex}>{i + 1}.</div>
                    <div className={styles.rowValueCountry}>
                      {countryName}
                      {flagUrl && (
                        <img
                          src={flagUrl}
                          alt="flag"
                          className={styles.flag}
                          width={20}
                          height={14}
                          style={{ marginLeft: 6 }}
                        />
                      )}
                    </div>
                    <div className={styles.rowValueAvarege}>
                      {Math.round(Number.parseFloat(medianPercentValue || '0'))}%
                    </div>
                  </button>
                );
              })}
          </div>
        )}

        {internationalId ? (
          <Comments
            filter={{
              entity: 'international_community',
              international: internationalId,
            }}
            title={false}
          />
        ) : null}
      </div>
    </div>
  );
}

export default InternationalContent;
